import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  InputAdornment,
  Toolbar,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Link,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Reply as ReplyIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkReadIcon,
  MarkEmailUnread as MarkUnreadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  timestamp: any;
  status: 'read' | 'unread' | 'replied';
  response?: string;
  respondedAt?: any;
  respondedBy?: string;
}

export const AdminContactMessages: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<ContactMessage[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [replyDialog, setReplyDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');

  // Check admin status
  useEffect(() => {
    const checkAccess = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const isAdmin = userData.isAdmin === true;
          
          if (!isAdmin) {
            setError('Access denied. Admin privileges required.');
            setTimeout(() => navigate('/dashboard'), 2000);
            return;
          }
        } else {
          setError('User profile not found.');
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setError('Failed to verify admin status.');
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      }

      setLoading(false);
    };

    checkAccess();
  }, [currentUser, navigate]);

  // Load messages
  const loadMessages = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const messagesQuery = query(
        collection(db, 'contact_messages'), 
        orderBy('timestamp', 'desc')
      );
      const messagesSnapshot = await getDocs(messagesQuery);
      const messagesData: ContactMessage[] = [];

      messagesSnapshot.forEach((doc) => {
        const messageData = doc.data();
        messagesData.push({
          id: doc.id,
          ...messageData,
        } as ContactMessage);
      });

      setMessages(messagesData);
      setFilteredMessages(messagesData);
    } catch (error) {
      console.error('Error loading messages:', error);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [currentUser]);

  // Filter messages
  useEffect(() => {
    let filtered = messages;

    if (searchTerm) {
      filtered = filtered.filter(
        (message) =>
          message.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          message.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          message.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
          message.message.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter((message) => message.status === filterStatus);
    }

    setFilteredMessages(filtered);
    setPage(0);
  }, [messages, searchTerm, filterStatus]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, message: ContactMessage) => {
    setAnchorEl(event.currentTarget);
    setSelectedMessage(message);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMessage(null);
  };

  const handleViewMessage = () => {
    setViewDialog(true);
    setAnchorEl(null); // Close menu but keep selectedMessage
  };

  const handleReplyToMessage = () => {
    setReplyDialog(true);
    setReplyText('');
    setAnchorEl(null); // Close menu but keep selectedMessage
  };

  const handleMarkAsRead = async () => {
    if (!selectedMessage) return;

    try {
      await updateDoc(doc(db, 'contact_messages', selectedMessage.id), {
        status: 'read',
      });
      
      setSuccess('Message marked as read');
      loadMessages();
    } catch (error) {
      console.error('Error updating message:', error);
      setError('Failed to update message');
    }
    handleMenuClose();
  };

  const handleMarkAsUnread = async () => {
    if (!selectedMessage) return;

    try {
      await updateDoc(doc(db, 'contact_messages', selectedMessage.id), {
        status: 'unread',
      });
      
      setSuccess('Message marked as unread');
      loadMessages();
    } catch (error) {
      console.error('Error updating message:', error);
      setError('Failed to update message');
    }
    handleMenuClose();
  };

  const handleSendReply = async () => {
    if (!selectedMessage || !replyText.trim()) return;

    try {
      await updateDoc(doc(db, 'contact_messages', selectedMessage.id), {
        status: 'replied',
        response: replyText.trim(),
        respondedAt: serverTimestamp(),
        respondedBy: currentUser?.displayName || currentUser?.email || 'Admin',
      });
      
      setSuccess('Reply saved successfully');
      setReplyDialog(false);
      setReplyText('');
      loadMessages();
    } catch (error) {
      console.error('Error saving reply:', error);
      setError('Failed to save reply');
    }
  };

  const handleDeleteMessage = async () => {
    if (!selectedMessage) return;

    try {
      await deleteDoc(doc(db, 'contact_messages', selectedMessage.id));
      setSuccess('Message deleted successfully');
      setDeleteDialog(false);
      loadMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      setError('Failed to delete message');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'unread': return 'error';
      case 'read': return 'warning';
      case 'replied': return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'unread': return 'Unread';
      case 'read': return 'Read';
      case 'replied': return 'Replied';
      default: return status;
    }
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'MMM dd, yyyy HH:mm');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 10, md: 12 }, pb: 4 }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            📧 Contact Messages
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and respond to messages received through the contact form
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="unread">Unread</MenuItem>
                <MenuItem value="read">Read</MenuItem>
                <MenuItem value="replied">Replied</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadMessages}
              sx={{ ml: 'auto' }}
            >
              Refresh
            </Button>
          </Toolbar>
        </Card>

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Contact Info</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell width={60}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMessages
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((message) => (
                    <TableRow key={message.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {message.name}
                          </Typography>
                          <Link href={`mailto:${message.email}`} underline="hover">
                            <Typography variant="caption" color="primary">
                              {message.email}
                            </Typography>
                          </Link>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {message.subject}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(message.status)}
                          color={getStatusColor(message.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimestamp(message.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, message)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={filteredMessages.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Card>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleViewMessage}>
            <EmailIcon sx={{ mr: 1 }} />
            View Message
          </MenuItem>
          <MenuItem onClick={handleReplyToMessage}>
            <ReplyIcon sx={{ mr: 1 }} />
            Add Reply
          </MenuItem>
          <Divider />
          {selectedMessage?.status !== 'read' && (
            <MenuItem onClick={handleMarkAsRead}>
              <MarkReadIcon sx={{ mr: 1 }} />
              Mark as Read
            </MenuItem>
          )}
          {selectedMessage?.status !== 'unread' && (
            <MenuItem onClick={handleMarkAsUnread}>
              <MarkUnreadIcon sx={{ mr: 1 }} />
              Mark as Unread
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={() => setDeleteDialog(true)} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete Message
          </MenuItem>
        </Menu>

        <Dialog open={viewDialog} onClose={() => { setViewDialog(false); setSelectedMessage(null); }} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <EmailIcon />
              Message Details
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedMessage && (
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">From:</Typography>
                  <Typography variant="body1">
                    {selectedMessage.name} ({selectedMessage.email})
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">Subject:</Typography>
                  <Typography variant="body1">{selectedMessage.subject}</Typography>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">Received:</Typography>
                  <Typography variant="body2">{formatTimestamp(selectedMessage.timestamp)}</Typography>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">Status:</Typography>
                  <Chip
                    label={getStatusLabel(selectedMessage.status)}
                    color={getStatusColor(selectedMessage.status) as any}
                    size="small"
                  />
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Message:</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedMessage.message}
                  </Typography>
                </Box>
                
                {selectedMessage.response && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Admin Response:
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                        {selectedMessage.response}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Replied by {selectedMessage.respondedBy} on {formatTimestamp(selectedMessage.respondedAt)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setViewDialog(false); setSelectedMessage(null); }}>Close</Button>
            <Button variant="contained" onClick={handleReplyToMessage}>
              Add Reply
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={replyDialog} onClose={() => { setReplyDialog(false); setSelectedMessage(null); }} maxWidth="md" fullWidth>
          <DialogTitle>Reply to Message</DialogTitle>
          <DialogContent>
            {selectedMessage && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Replying to: {selectedMessage.name} ({selectedMessage.email})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Subject: {selectedMessage.subject}
                </Typography>
              </Box>
            )}
            <TextField
              autoFocus
              multiline
              rows={6}
              fullWidth
              label="Your Reply"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your response here..."
              helperText="This reply will be saved to the message record for reference."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setReplyDialog(false); setSelectedMessage(null); }}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleSendReply}
              disabled={!replyText.trim()}
            >
              Save Reply
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle>Delete Message</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this message? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={handleDeleteMessage}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}; 